import { useMemo } from 'react'
import {
   CartesianGrid,
   Line,
   LineChart,
   ResponsiveContainer,
   Tooltip,
   XAxis,
   YAxis,
} from 'recharts'

interface VolumeDiscountProps {
   contractLength: number
   productPEPMBaseCost?: number
   productMinimumCharge?: number
   basePrice?: number
   basePEPMPrice?: number
   termContractFeature: string[] | null
   userRole: string
   employeeCount: number
   formType: number
   limitVolumeDiscount?: boolean
}

/**
 * Shared calculation logic for volume discount data
 */
const useVolumeDiscountCalculation = (
   effectiveBasePrice: number,
   effectiveBasePEPMPrice: number,
   productMinimumCharge: number,
   employeeCounts: number[] = [25, 100, 200, 500, 1000, 2500],
   limitVolumeDiscount: boolean = false,
) => {
   return useMemo(() => {
      return employeeCounts.map((productEECount) => {
         // Calculate the discounted price using the formula
         const volumeDiscountedPEPM = Math.min(
            ((15.1 * Math.pow(productEECount, 0.84)) / (productEECount * 9.0)) *
               effectiveBasePrice,
            effectiveBasePrice,
         )
         // 50% volume discount cap: only applied if LimitVolumeDiscount permission is set
         const finalPEPM = limitVolumeDiscount
            ? Math.max(volumeDiscountedPEPM, effectiveBasePrice * 0.5)
            : volumeDiscountedPEPM

         const discountedPrice = Math.max(
            productMinimumCharge / productEECount,
            finalPEPM,
         )

         // Calculate the discount percentage
         const discountPercentage =
            ((effectiveBasePEPMPrice - discountedPrice) /
               effectiveBasePEPMPrice) *
            100

         // Ensure valid percentage (handle edge cases)
         const validDiscountPercentage =
            isNaN(discountPercentage) || !isFinite(discountPercentage)
               ? 0
               : Math.max(0, Math.min(100, discountPercentage))

         // Calculate the monthly savings
         const monthlySavings =
            (effectiveBasePEPMPrice - discountedPrice) * productEECount

         // Calculate annual savings
         const annualSavings = monthlySavings * 12

         return {
            employeeCount: productEECount,
            discountedPrice: discountedPrice.toFixed(2),
            discountPercentage: validDiscountPercentage.toFixed(2),
            monthlySavings: monthlySavings.toFixed(2),
            annualSavings: annualSavings.toFixed(2),
         }
      })
   }, [
      effectiveBasePrice,
      effectiveBasePEPMPrice,
      productMinimumCharge,
      employeeCounts,
      limitVolumeDiscount,
   ])
}

/**
 * Graph component for the volume discount calculator
 */
function VolumeDiscountGraph({
   contractLength,
   productPEPMBaseCost = 9.0,
   productMinimumCharge = 100.0,
   basePrice,
   basePEPMPrice,
   termContractFeature,
   userRole,
   employeeCount,
   formType,
   limitVolumeDiscount = false,
}: VolumeDiscountProps) {
   // Check if the user has access to the term contract feature
   if (!termContractFeature?.includes(userRole)) {
      return null
   }
   // Use provided base prices or fall back to the product prices
   const effectiveBasePrice = basePrice || productPEPMBaseCost
   const effectiveBasePEPMPrice = basePEPMPrice || productPEPMBaseCost

   // Use specific points that match our desired tick marks
   let employeeCounts: number[] = []
   let ticks: number[] = []
   let xAxisDomain: [number, number] = [25, 800] // Default domain
   let yAxisDomain: [number, number] = [0, 100] // Default domain

   if (employeeCount <= 500) {
      // Generate more data points for a smoother curve (every 10 employees)
      employeeCounts = Array.from({ length: 78 }, (_, i) => 25 + i * 10)
      ticks = [25, 100, 200, 300, 400, 500, 600, 700, 800]
      xAxisDomain = [25, 800] // Show full range up to 800
      yAxisDomain = [0, 45]
   } else {
      // Generate more data points for a smoother curve (every 25 employees) up to 2000
      employeeCounts = Array.from({ length: 80 }, (_, i) => 25 + i * 25)
      ticks = [25, 500, 1000, 1500, 2000]
      xAxisDomain = [25, 2000] // Cap display at 2000 employees
      yAxisDomain = [0, 60]
   }

   // Use the shared calculation hook
   const discountData = useVolumeDiscountCalculation(
      effectiveBasePrice,
      effectiveBasePEPMPrice,
      productMinimumCharge,
      employeeCounts,
      limitVolumeDiscount,
   )
   if (employeeCount <= 2000) {
      return (
         <div className='volume-discount-graph nobreak'>
            <div
               className='volume-discount-graph-container'
               style={{ marginTop: '-20px' }}
            >
               <h5
                  className='contractLengthBox dataLabel'
                  style={{ marginBottom: '-1rem', whiteSpace: 'nowrap' }}
               >
                  Volume Discount Scale
               </h5>
               <LineChart
                  width={300}
                  height={225}
                  data={discountData
                     .map((data) => ({
                        employeeCount: data.employeeCount,
                        discountPercentage: parseFloat(data.discountPercentage),
                     }))
                     .filter(
                        (data) =>
                           !isNaN(data.discountPercentage) &&
                           isFinite(data.discountPercentage) &&
                           data.discountPercentage >= 0,
                     )}
                  margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
               >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis
                     type='number'
                     domain={xAxisDomain}
                     dataKey='employeeCount'
                     ticks={ticks}
                     tick={{ fill: '#686868' }}
                     axisLine={{ stroke: '#686868' }}
                     tickLine={{ stroke: '#686868' }}
                     label={{
                        value: 'Employee Count',
                        position: 'bottom',
                        offset: 0,
                        style: { fill: '#686868' },
                     }}
                  />
                  <YAxis
                     domain={yAxisDomain}
                     label={{
                        value: 'Discount %',
                        angle: -90,
                        position: 'left',
                        offset: -15,
                        style: { textAnchor: 'middle', fill: '#686868' },
                     }}
                  />
                  <Tooltip
                     formatter={(value) => [
                        `${Number(value).toFixed(1)}%`,
                        'Discount',
                     ]}
                  />
                  <Line
                     type='monotoneX'
                     dataKey='discountPercentage'
                     stroke='gray'
                     strokeWidth={2}
                     name='Discount %'
                     dot={false}
                     activeDot={false}
                     connectNulls={false}
                     isAnimationActive={false}
                  />
               </LineChart>
            </div>

            <p
               className='productDescription'
               style={{ fontSize: '0.9em', fontStyle: 'italic' }}
            >
               Volume discounts are automatically applied based on employee
               count. The graph above shows estimated savings at different
               employee counts.
            </p>
         </div>
      )
   } else {
      return <></>
   }
}

export { VolumeDiscountGraph }
